import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { Container } from '../components/common/Container';

const TermsWrapper = styled.div`
  padding: ${theme.spacing['4xl']} 0;
  min-height: 80vh;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['4xl']};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const LastUpdated = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
  margin-bottom: ${theme.spacing['2xl']};
  padding-bottom: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.borderLight};
`;

const Section = styled.section`
  margin-bottom: ${theme.spacing['2xl']};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.text.primary};
`;

const SubsectionTitle = styled.h3`
  font-size: ${theme.fontSizes.xl};
  margin-bottom: ${theme.spacing.sm};
  margin-top: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const Paragraph = styled.p`
  color: ${theme.colors.text.secondary};
  line-height: 1.8;
  margin-bottom: ${theme.spacing.md};
`;

const List = styled.ul`
  color: ${theme.colors.text.secondary};
  line-height: 1.8;
  margin-bottom: ${theme.spacing.md};
  padding-left: ${theme.spacing.xl};
`;

const ListItem = styled.li`
  margin-bottom: ${theme.spacing.sm};
`;

const HighlightBox = styled.div`
  background-color: ${theme.colors.primaryLight}20;
  border-left: 4px solid ${theme.colors.primary};
  padding: ${theme.spacing.lg};
  margin: ${theme.spacing.xl} 0;
  border-radius: ${theme.radii.md};
`;

const WarningBox = styled.div`
  background-color: ${theme.colors.warning}20;
  border-left: 4px solid ${theme.colors.warning};
  padding: ${theme.spacing.lg};
  margin: ${theme.spacing.xl} 0;
  border-radius: ${theme.radii.md};
`;

export const TermsPage: React.FC = () => {
  return (
    <TermsWrapper>
      <Container>
        <Content>
          <Title>Terms of Service</Title>
          <LastUpdated>Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</LastUpdated>

          <HighlightBox>
            <Paragraph style={{ margin: 0, color: theme.colors.text.primary }}>
              <strong>TL;DR:</strong> Promptasaurus is currently free while in development. Use it responsibly, 
              don't abuse it, and respect others' rights. We're not responsible for how you use AI-generated content. 
              Your prompts are yours, but improvements we make are ours to use for service enhancement.
            </Paragraph>
          </HighlightBox>

          <Section>
            <SectionTitle>1. Acceptance of Terms</SectionTitle>
            <Paragraph>
              By accessing or using Promptasaurus ("Service"), operated by Gruntwork.ai ("we," "our," or "us"), 
              you agree to be bound by these Terms of Service ("Terms"). If you don't agree to these Terms, 
              please don't use our Service.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>2. Description of Service</SectionTitle>
            <Paragraph>
              Promptasaurus provides AI-powered prompt enhancement tools including:
            </Paragraph>
            <List>
              <ListItem>Fossil Fills - Mad Libs-style prompt creation system</ListItem>
              <ListItem>Prompt improvement and optimization services</ListItem>
              <ListItem>Browser extension for AI platforms (in development)</ListItem>
              <ListItem>Template library and management tools</ListItem>
            </List>
            <Paragraph>
              The Service is currently in active development and features may change, be added, or removed 
              without notice.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>3. Account Registration</SectionTitle>
            <Paragraph>
              To access certain features, you may need to create an account. You agree to:
            </Paragraph>
            <List>
              <ListItem>Provide accurate and complete information</ListItem>
              <ListItem>Maintain the security of your account credentials</ListItem>
              <ListItem>Promptly update any changes to your information</ListItem>
              <ListItem>Accept responsibility for all activities under your account</ListItem>
              <ListItem>Notify us immediately of any unauthorized access</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>4. Acceptable Use</SectionTitle>
            
            <SubsectionTitle>You MAY:</SubsectionTitle>
            <List>
              <ListItem>Use the Service for personal and commercial purposes</ListItem>
              <ListItem>Create, save, and share prompt templates</ListItem>
              <ListItem>Integrate the Service with AI platforms as intended</ListItem>
              <ListItem>Provide feedback and suggestions for improvement</ListItem>
            </List>

            <SubsectionTitle>You MAY NOT:</SubsectionTitle>
            <List>
              <ListItem>Use the Service for illegal or unauthorized purposes</ListItem>
              <ListItem>Generate content that violates any laws or regulations</ListItem>
              <ListItem>Create harmful, abusive, or discriminatory content</ListItem>
              <ListItem>Attempt to reverse engineer or hack the Service</ListItem>
              <ListItem>Overwhelm the Service with automated requests</ListItem>
              <ListItem>Resell or redistribute the Service without permission</ListItem>
              <ListItem>Impersonate others or misrepresent your affiliation</ListItem>
              <ListItem>Use the Service to spam or harass others</ListItem>
              <ListItem>Copy, modify, or distribute our proprietary code or content</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>5. Intellectual Property</SectionTitle>
            
            <SubsectionTitle>Your Content</SubsectionTitle>
            <Paragraph>
              You retain ownership of the prompts and content you create. By using our Service, you grant us a 
              limited license to process and improve your prompts as necessary to provide the Service.
            </Paragraph>

            <SubsectionTitle>Our Service</SubsectionTitle>
            <Paragraph>
              The Promptasaurus Service, including its code, features, functionality, design, and improvements 
              generated, is owned by Gruntwork.ai and is protected by copyright, trademark, and other intellectual 
              property laws. All rights reserved.
            </Paragraph>

            <SubsectionTitle>Feedback</SubsectionTitle>
            <Paragraph>
              Any feedback, suggestions, or ideas you provide may be used by us without any obligation to you.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>6. Privacy and Data</SectionTitle>
            <Paragraph>
              Your use of the Service is also governed by our Privacy Policy. By using the Service, 
              you consent to the collection and use of information as described in the Privacy Policy.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>7. Third-Party Services</SectionTitle>
            <Paragraph>
              Promptasaurus integrates with third-party AI services (ChatGPT, Claude, etc.). We are not 
              responsible for:
            </Paragraph>
            <List>
              <ListItem>The availability or functionality of third-party services</ListItem>
              <ListItem>Content generated by third-party AI models</ListItem>
              <ListItem>Your compliance with third-party terms of service</ListItem>
              <ListItem>Data handling by third-party services</ListItem>
            </List>
          </Section>

          <WarningBox>
            <SectionTitle style={{ marginTop: 0 }}>8. Disclaimer of Warranties</SectionTitle>
            <Paragraph>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT:
            </Paragraph>
            <List>
              <ListItem>The Service will be uninterrupted or error-free</ListItem>
              <ListItem>Defects will be corrected</ListItem>
              <ListItem>The Service is free of viruses or harmful components</ListItem>
              <ListItem>Results from the Service will meet your requirements</ListItem>
            </List>
            <Paragraph>
              USE OF THE SERVICE IS AT YOUR OWN RISK.
            </Paragraph>
          </WarningBox>

          <Section>
            <SectionTitle>9. Limitation of Liability</SectionTitle>
            <Paragraph>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, GRUNTWORK.AI SHALL NOT BE LIABLE FOR ANY 
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT 
              LIMITED TO:
            </Paragraph>
            <List>
              <ListItem>Loss of profits, data, or use</ListItem>
              <ListItem>Business interruption</ListItem>
              <ListItem>Damage resulting from use or inability to use the Service</ListItem>
              <ListItem>Content generated through the Service</ListItem>
              <ListItem>Unauthorized access to your data</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>10. Indemnification</SectionTitle>
            <Paragraph>
              You agree to indemnify and hold harmless Gruntwork.ai from any claims, damages, losses, 
              and expenses (including legal fees) arising from:
            </Paragraph>
            <List>
              <ListItem>Your use of the Service</ListItem>
              <ListItem>Your violation of these Terms</ListItem>
              <ListItem>Your violation of any rights of another party</ListItem>
              <ListItem>Content you create or generate using the Service</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>11. Modifications to Service and Terms</SectionTitle>
            <Paragraph>
              We reserve the right to:
            </Paragraph>
            <List>
              <ListItem>Modify or discontinue the Service at any time</ListItem>
              <ListItem>Change these Terms with notice</ListItem>
              <ListItem>Introduce fees for previously free features</ListItem>
              <ListItem>Set limits on usage or storage</ListItem>
            </List>
            <Paragraph>
              Continued use after changes constitutes acceptance of the modified Terms.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>12. Termination</SectionTitle>
            <Paragraph>
              We may terminate or suspend your access to the Service immediately, without prior notice, for:
            </Paragraph>
            <List>
              <ListItem>Violation of these Terms</ListItem>
              <ListItem>Fraudulent or illegal activity</ListItem>
              <ListItem>Behavior harmful to other users or the Service</ListItem>
            </List>
            <Paragraph>
              You may terminate your account at any time through the account settings or by contacting us.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>13. Governing Law</SectionTitle>
            <Paragraph>
              These Terms are governed by the laws of the United States and the State of California, 
              without regard to conflict of law principles. Any disputes shall be resolved in the courts 
              of California.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>14. Severability</SectionTitle>
            <Paragraph>
              If any provision of these Terms is found to be unenforceable, the remaining provisions 
              will continue in full force and effect.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>15. Contact Information</SectionTitle>
            <Paragraph>
              For questions about these Terms, please contact us:
            </Paragraph>
            <List>
              <ListItem>Email: legal@gruntwork.ai</ListItem>
              <ListItem>Website: https://promptasaurus.ai/contact</ListItem>
              <ListItem>Address: Gruntwork.ai, California, USA</ListItem>
            </List>
          </Section>

          <HighlightBox>
            <Paragraph style={{ margin: 0 }}>
              <strong>Beta Notice:</strong> Promptasaurus is currently in beta. We appreciate your patience 
              as we continue to improve and expand the service. Your feedback helps us build a better product!
            </Paragraph>
          </HighlightBox>
        </Content>
      </Container>
    </TermsWrapper>
  );
};